/**
 * components/BibEditeurV2.js
 * The Crowd v2 — Editeur de fiches bibliotheque.
 *
 * Copie v2 de pages/BibliothequeEditor.js : meme logique, balisage et
 * classes v2 (styles dans v2-bibliotheques.css, aucun style en ligne).
 * - Fichiers .html : editeur WYSIWYG TipTap (procedures IT)
 * - Fichiers .md   : editeur Markdown split source / apercu
 * - Creation       : champ Titre (devient le nom du fichier .html)
 *
 * Raccourcis : Ctrl+S sauvegarder ; Echap annuler et Tab indenter
 * (editeur Markdown uniquement).
 *
 * Correction par rapport a l'original : le composant principal ne
 * transmettait pas `collaborateur` a l'editeur HTML. Les metadonnees
 * « Cree par / Modifie par » recevaient donc l'identifiant numerique au
 * lieu du nom. Il est desormais transmis.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import IconeV2 from "./IconeV2";
import { ApercuMarkdown } from "./BibRenduFiche";
import { injectMeta, readHtmlMeta, stripMeta } from "../services/bibliotheque";

// ----------------------------------------------------------------------------
// Utilitaires
// ----------------------------------------------------------------------------

function slugifyTitle(title) {
  return title
    .trim()
    .toLowerCase()
    .replace(/[àâä]/g, "a")
    .replace(/[éèêë]/g, "e")
    .replace(/[îï]/g, "i")
    .replace(/[ôö]/g, "o")
    .replace(/[ùûü]/g, "u")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const PROCEDURE_TEMPLATE = `
<h2>Contexte / Objectif</h2>
<p>Décrivez ici le contexte et l'objectif de la procédure.</p>

<h2>Prérequis</h2>
<ul>
  <li>Prérequis 1</li>
  <li>Prérequis 2</li>
</ul>

<h2>Étapes</h2>
<ol>
  <li>Étape 1</li>
  <li>Étape 2</li>
  <li>Étape 3</li>
</ol>

<h2>Notes / Avertissements</h2>
<blockquote><p>Ajoutez ici toute note ou avertissement important.</p></blockquote>
`.trim();

// ----------------------------------------------------------------------------
// Barre d'outils TipTap
// ----------------------------------------------------------------------------

function BoutonOutil({ onAction, actif = false, libelle, titre, children }) {
  return (
    <button
      type="button"
      className={`v2-edit-outil${actif ? " actif" : ""}`}
      aria-pressed={actif}
      aria-label={titre || libelle}
      title={titre || libelle}
      onMouseDown={(e) => { e.preventDefault(); onAction(); }}
    >
      {children || libelle}
    </button>
  );
}

function Separateur() {
  return <span className="v2-edit-separateur" aria-hidden="true" />;
}

function BarreOutilsTipTap({ editor, onImageUpload }) {
  if (!editor) return null;
  const c = () => editor.chain().focus();

  const ajouterLien = () => {
    const url = window.prompt("URL du lien :");
    if (url) c().setLink({ href: url }).run();
  };

  return (
    <div className="v2-edit-outils" role="toolbar" aria-label="Mise en forme">
      <BoutonOutil libelle="G" titre="Gras" actif={editor.isActive("bold")} onAction={() => c().toggleBold().run()} />
      <BoutonOutil libelle="I" titre="Italique" actif={editor.isActive("italic")} onAction={() => c().toggleItalic().run()} />
      <BoutonOutil libelle="S" titre="Barré" actif={editor.isActive("strike")} onAction={() => c().toggleStrike().run()} />
      <BoutonOutil libelle="<>" titre="Code" actif={editor.isActive("code")} onAction={() => c().toggleCode().run()} />
      <Separateur />
      <BoutonOutil libelle="H1" titre="Titre 1" actif={editor.isActive("heading", { level: 1 })} onAction={() => c().toggleHeading({ level: 1 }).run()} />
      <BoutonOutil libelle="H2" titre="Titre 2" actif={editor.isActive("heading", { level: 2 })} onAction={() => c().toggleHeading({ level: 2 }).run()} />
      <BoutonOutil libelle="H3" titre="Titre 3" actif={editor.isActive("heading", { level: 3 })} onAction={() => c().toggleHeading({ level: 3 }).run()} />
      <Separateur />
      <BoutonOutil libelle="• Liste" titre="Liste à puces" actif={editor.isActive("bulletList")} onAction={() => c().toggleBulletList().run()} />
      <BoutonOutil libelle="1. Liste" titre="Liste numérotée" actif={editor.isActive("orderedList")} onAction={() => c().toggleOrderedList().run()} />
      <BoutonOutil libelle="❝" titre="Citation" actif={editor.isActive("blockquote")} onAction={() => c().toggleBlockquote().run()} />
      <BoutonOutil libelle="{ }" titre="Bloc de code" actif={editor.isActive("codeBlock")} onAction={() => c().toggleCodeBlock().run()} />
      <Separateur />
      <BoutonOutil titre="Lien" actif={editor.isActive("link")} onAction={ajouterLien}>
        <IconeV2 nom="lien" taille={13} /> Lien
      </BoutonOutil>
      <BoutonOutil titre="Image" onAction={onImageUpload}>
        <IconeV2 nom="image" taille={13} /> Image
      </BoutonOutil>
      <Separateur />
      <BoutonOutil titre="Annuler la saisie" onAction={() => c().undo().run()}>
        <IconeV2 nom="annuler" taille={13} />
      </BoutonOutil>
      <BoutonOutil titre="Rétablir" onAction={() => c().redo().run()}>
        <IconeV2 nom="refaire" taille={13} />
      </BoutonOutil>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Barre d'actions commune (titre, statut, annuler, sauvegarder)
// ----------------------------------------------------------------------------

function BarreActions({ intitule, modifie, enCours, onCancel, onSave }) {
  return (
    <div className="v2-edit-barre">
      <span className="v2-edit-intitule">
        {intitule}
        {modifie && <span className="v2-edit-non-sauve">non sauvegardé</span>}
      </span>
      <div className="v2-edit-barre-actions">
        <kbd className="v2-raccourci">Ctrl+S</kbd>
        <button type="button" className="v2-btn v2-btn-secondaire" onClick={onCancel}>Annuler</button>
        <button type="button" className="v2-btn v2-btn-principal" onClick={onSave} disabled={enCours || !modifie}>
          {enCours ? "Sauvegarde…" : "Sauvegarder"}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Editeur WYSIWYG HTML (TipTap)
// ----------------------------------------------------------------------------

function EditeurHtml({ content, title, isNew, onSave, onCancel, slug, collaborateurId, collaborateur, uploadImageBibliotheque }) {
  const [titreValue, setTitreValue] = useState(title || "");
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const existingMeta = readHtmlMeta(content || "");
  const pureContent = stripMeta(content || "");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: "Rédigez votre procédure ici…" }),
    ],
    content: pureContent || (isNew ? PROCEDURE_TEMPLATE : ""),
    onUpdate: () => setHasChanges(true),
  });

  // Correspondance URL SAS -> chemin Blob, pour la sauvegarde
  const sasMapRef = useRef({});

  function replaceSasWithBlobPaths(html) {
    let result = html;
    for (const [sasUrl, blobPath] of Object.entries(sasMapRef.current)) {
      result = result.split(sasUrl).join(blobPath);
    }
    return result;
  }

  const handleSave = useCallback(async () => {
    setError(null);
    if (isNew && !titreValue.trim()) {
      setError("Le titre est obligatoire.");
      return;
    }
    const html = replaceSasWithBlobPaths(editor?.getHTML() || "");
    const fileName = isNew ? `${slugifyTitle(titreValue)}.html` : null;
    const now = new Date().toISOString();
    const auteur = collaborateur ? `${collaborateur.prenom} ${collaborateur.nom}` : String(collaborateurId);

    const meta = isNew
      ? { created_at: now, created_by: auteur, updated_at: now, updated_by: auteur }
      : { ...existingMeta, updated_at: now, updated_by: auteur };

    setSaving(true);
    try {
      await onSave(injectMeta(html, meta), fileName);
      setHasChanges(false);
    } catch (e) {
      setError(e.message || "Erreur sauvegarde.");
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, titreValue, isNew, onSave, collaborateur, collaborateurId, existingMeta]);

  // Ctrl+S
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  const handleImageUpload = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    try {
      const { sasUrl, blobPath } = await uploadImageBibliotheque(slug, file, collaborateurId);
      sasMapRef.current[sasUrl] = `blob-img:${blobPath}`;
      editor.chain().focus().setImage({ src: sasUrl, alt: file.name }).run();
      setHasChanges(true);
    } catch (err) {
      setError("Erreur upload image : " + (err.message || ""));
    }
    e.target.value = "";
  };

  return (
    <div className="v2-edit">
      <BarreActions
        intitule={isNew ? "Nouvelle procédure" : `Édition : ${title}`}
        modifie={hasChanges}
        enCours={saving}
        onCancel={onCancel}
        onSave={handleSave}
      />

      {isNew && (
        <div className="v2-edit-titre-zone">
          <label htmlFor="v2-edit-titre" className="v2-invisible">Titre de la procédure</label>
          <input
            id="v2-edit-titre"
            type="text"
            className="v2-edit-titre"
            placeholder="Titre de la procédure *"
            value={titreValue}
            onChange={(e) => { setTitreValue(e.target.value); setHasChanges(true); }}
            autoFocus
          />
        </div>
      )}

      {error && <div className="v2-msg v2-msg-erreur v2-edit-erreur">{error}</div>}

      <BarreOutilsTipTap editor={editor} onImageUpload={handleImageUpload} />

      <div className="v2-edit-zone">
        <div className="v2-bib-contenu">
          <EditorContent editor={editor} />
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" className="v2-invisible" tabIndex={-1} onChange={handleFileChange} />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Editeur Markdown split source / apercu
// ----------------------------------------------------------------------------

function EditeurMarkdown({ content, filePath, onSave, onCancel }) {
  const [editContent, setEditContent] = useState(content || "");
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = useCallback((e) => {
    setEditContent(e.target.value);
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave(editContent);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  }, [editContent, onSave]);

  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") onCancel();
    if (e.key === "Tab") {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const newContent = editContent.substring(0, start) + "  " + editContent.substring(end);
      setEditContent(newContent);
      setHasChanges(true);
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 2;
      }, 0);
    }
  }, [editContent, handleSave, onCancel]);

  const fileName = filePath ? filePath.split("/").pop() : "";

  return (
    <div className="v2-edit">
      <BarreActions
        intitule={`Édition : ${fileName}`}
        modifie={hasChanges}
        enCours={saving}
        onCancel={onCancel}
        onSave={handleSave}
      />

      <div className="v2-edit-split">
        <div className="v2-edit-volet">
          <div className="v2-edit-volet-titre">Source Markdown</div>
          <label htmlFor="v2-edit-source" className="v2-invisible">Source Markdown</label>
          <textarea
            id="v2-edit-source"
            className="v2-edit-source"
            value={editContent}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            autoFocus
          />
        </div>
        <div className="v2-edit-volet">
          <div className="v2-edit-volet-titre">Aperçu</div>
          <div className="v2-edit-apercu">
            <ApercuMarkdown source={editContent} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Composant principal : choisit l'editeur selon le format
// ----------------------------------------------------------------------------

export default function BibEditeurV2({
  content,
  filePath,
  isNew,
  slug,
  collaborateurId,
  collaborateur,
  uploadImageBibliotheque,
  onSave,
  onCancel,
}) {
  const fileName = filePath ? filePath.split("/").pop() : "";
  const isHtml = isNew || fileName.endsWith(".html");

  if (isHtml) {
    return (
      <EditeurHtml
        content={content}
        title={fileName.replace(/\.html$/, "")}
        isNew={isNew}
        slug={slug}
        collaborateurId={collaborateurId}
        collaborateur={collaborateur}
        uploadImageBibliotheque={uploadImageBibliotheque}
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }

  return <EditeurMarkdown content={content} filePath={filePath} onSave={onSave} onCancel={onCancel} />;
}
